/** Enumerations shared by the API and both frontends. */

export const USER_ROLES = /** @type {const} */ (['owner', 'agent', 'admin']);

export const SENDER_TYPES = /** @type {const} */ (['agent', 'visitor', 'system']);

export const CONVERSATION_STATUSES = /** @type {const} */ ([
  'open',
  'pending',
  'resolved',
  'closed',
]);

export const TICKET_STATUSES = /** @type {const} */ ([
  'open',
  'in_progress',
  'waiting',
  'resolved',
  'closed',
]);

export const TICKET_PRIORITIES = /** @type {const} */ (['low', 'normal', 'high', 'urgent']);

export const SUBSCRIBER_TYPES = /** @type {const} */ (['agent', 'visitor']);

/**
 * What kind of thing the widget is embedded in.
 *
 * `web` is any site the merchant controls. `shopify` is an embedded Shopify
 * app, which is different in one way that matters: the store is identified by
 * a `.myshopify.com` domain everywhere in Shopify's own tooling, so the app is
 * named after that rather than after whatever the agency felt like typing.
 */
export const APP_PLATFORMS = /** @type {const} */ (['web', 'shopify']);

export const DEFAULT_APP_PLATFORM = 'web';

/**
 * The two notification channels that leave this system through someone else's
 * network — a browser's push service and Slack's API — and are therefore the
 * two that can fail for reasons nothing here controls.
 *
 * Email is not among them: Resend accepts a message and owns delivery from
 * there, so there is no per-recipient outcome to retry against. Socket.IO is
 * not either: a socket that is not connected is not a failed delivery, it is
 * the absence of one, and push exists precisely to cover that.
 */
export const DELIVERY_CHANNELS = /** @type {const} */ (['push', 'slack']);

/**
 * `pending` is waiting on its first or a later attempt; `sent` got through;
 * `failed` used every attempt and stopped; `dead` hit something no retry can
 * fix — a browser that unsubscribed, a Slack token that was revoked.
 *
 * The split between the last two is what keeps the failure list worth reading:
 * `failed` means "this may be worth another look", `dead` means "this endpoint
 * is gone and nothing is owed to it".
 */
export const DELIVERY_STATUSES = /** @type {const} */ ([
  'pending',
  'sent',
  'failed',
  'dead',
]);

/** The first attempt plus three retries. */
export const DELIVERY_MAX_ATTEMPTS = 4;

/**
 * How long to wait before each retry, indexed by the number of attempts
 * already made. Spread wide enough that a provider having a bad minute has
 * actually recovered by the last one, rather than being hit three times inside
 * the same outage.
 */
export const DELIVERY_BACKOFF_MS = [30_000, 2 * 60_000, 10 * 60_000];

export const EVENT_TYPES = /** @type {const} */ ([
  'conversation.created',
  'conversation.status_changed',
  'conversation.assigned',
  'ticket.assigned',
  'message.sent',
  'message.read',
  // An agent rewrote or withdrew something they had already sent. Both leave a
  // trail row: the visitor may well have read the original, so "what did we
  // actually tell them" has to stay answerable after the bubble changes.
  'message.edited',
  'message.deleted',
  'ticket.created',
  'ticket.updated',
  'doc.published',
  'push.sent',
  'push.pruned',
  // A tracked delivery that used every attempt, and one that got through on a
  // later one. Both are worth a row in the trail: the first is something an
  // operator may need to act on, the second is the retry earning its keep.
  'delivery.failed',
  'delivery.recovered',
  'attachment.pruned',
  'widget.session_started',
  'email.sent',
  'member.invited',
  'member.joined',
  'member.updated',
  'member.removed',
  'app.transfer_requested',
  'app.transfer_confirmed',
  'app.transfer_cancelled',
]);

/**
 * Per-app access, granted to a member by the app's owner.
 *
 * Each scope is an *ordered* ladder rather than a set of booleans, so "can
 * send" always implies "can read" and there is no way to express the incoherent
 * combination of the two. `none` is always index 0, which makes the default
 * for an unknown scope deny rather than allow.
 */
export const PERMISSION_SCOPES = /** @type {const} */ ({
  chat: ['none', 'read', 'send'],
  docs: ['none', 'read', 'write'],
  analytics: ['none', 'read'],
  integration: ['none', 'read', 'write'],
});

export const PERMISSION_KEYS = Object.keys(PERMISSION_SCOPES);

/** What a newly invited member gets unless the inviter changes it. */
export const DEFAULT_PERMISSIONS = {
  chat: 'send',
  docs: 'read',
  analytics: 'read',
  integration: 'none',
};

/** The owner's implicit grant — every scope at its highest level. */
export const FULL_PERMISSIONS = Object.fromEntries(
  Object.entries(PERMISSION_SCOPES).map(([scope, levels]) => [scope, levels.at(-1)])
);

export const NO_PERMISSIONS = Object.fromEntries(
  PERMISSION_KEYS.map((scope) => [scope, 'none'])
);

/**
 * True when `permissions` reaches at least `level` on `scope`.
 *
 * Unknown scopes and unknown levels both deny: a typo in a route guard must
 * fail closed, never open.
 */
export function hasPermission(permissions, scope, level) {
  const ladder = PERMISSION_SCOPES[scope];
  if (!ladder) return false;

  const required = ladder.indexOf(level);
  if (required <= 0) return required === 0; // 'none' is satisfied by anything

  const granted = ladder.indexOf(permissions?.[scope]);
  return granted >= required;
}

/** Drops unknown scopes and clamps unknown levels to `none`. */
export function normalizePermissions(input) {
  return Object.fromEntries(
    Object.entries(PERMISSION_SCOPES).map(([scope, levels]) => [
      scope,
      levels.includes(input?.[scope]) ? input[scope] : 'none',
    ])
  );
}

/**
 * How loudly the dashboard alerts its agent, as one choice rather than a pile
 * of independent switches.
 *
 * The two ringing modes differ only in what counts as "still waiting":
 * `continuous` rings while anything is unread, `critical` narrows that to
 * threads whose visitor is still on the page — the case where someone is
 * sitting there watching a reply not arrive. Both stop when the thread is
 * read, which is the dismissal.
 */
export const ALERT_MODES = /** @type {const} */ ([
  'normal',
  'continuous',
  'critical',
  'silent',
]);

export const DEFAULT_ALERT_MODE = 'normal';

/** Modes that keep sounding until the agent reads the thread. */
export const RINGING_ALERT_MODES = ['continuous', 'critical'];

/** Statuses that mean "no longer needs an agent". */
export const CLOSED_CONVERSATION_STATUSES = ['resolved', 'closed'];
export const CLOSED_TICKET_STATUSES = ['resolved', 'closed'];

/**
 * Canned replies, triggered by typing `/key` in the composer.
 *
 * These are what a new agent starts with, not a fixed list: shortcuts are
 * stored per agent and are theirs to add to, edit and delete. Kept here so the
 * API can seed them and the dashboard can offer to restore them.
 */
export const DEFAULT_SHORTCUTS = [
  {
    key: 'known-issue',
    text: "This is a known issue on our side, not your configuration — we've reproduced it and a fix is in progress.",
  },
  {
    key: 'rollback-eta',
    text: "We're rolling back the deploy that caused this now. Expect it clear within 10 minutes; I'll confirm here the moment it is.",
  },
  {
    key: 'ask-for-order-id',
    text: 'Could you send me an order ID where you saw this? It lets me pull the exact request logs for that checkout.',
  },
  {
    key: 'escalate-tier3',
    text: "I'm escalating this to our engineering on-call now and staying on the thread with you until it's resolved.",
  },
];

/** The trigger is what gets typed after `/`, so it has to be typeable. */
export const SHORTCUT_KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,31}$/;

export const LIMITS = {
  SHORTCUTS_MAX: 60,
  SHORTCUT_TEXT_MAX: 2000,
  MESSAGE_BODY_MAX: 8000,
  PAGE_SIZE_DEFAULT: 25,
  PAGE_SIZE_MAX: 100,
  PUSH_PREVIEW_LENGTH: 120,
  /**
   * Custom visitor fields the merchant's snippet may pass. Shared rather than
   * server-local because the Integration view documents these numbers to the
   * merchant, and a limit that is described differently from how it is
   * enforced is worse than one that is not described at all.
   */
  VISITOR_METADATA_MAX_KEYS: 30,
  VISITOR_METADATA_KEY_MAX: 60,
  VISITOR_METADATA_VALUE_MAX: 200,
  VISITOR_TOKEN_TTL_SECONDS: 60 * 60 * 12,
};

export default {
  USER_ROLES,
  PERMISSION_SCOPES,
  PERMISSION_KEYS,
  DEFAULT_PERMISSIONS,
  FULL_PERMISSIONS,
  NO_PERMISSIONS,
  hasPermission,
  normalizePermissions,
  SENDER_TYPES,
  CONVERSATION_STATUSES,
  TICKET_STATUSES,
  TICKET_PRIORITIES,
  SUBSCRIBER_TYPES,
  DELIVERY_CHANNELS,
  DELIVERY_STATUSES,
  DELIVERY_MAX_ATTEMPTS,
  DELIVERY_BACKOFF_MS,
  APP_PLATFORMS,
  DEFAULT_APP_PLATFORM,
  EVENT_TYPES,
  ALERT_MODES,
  DEFAULT_ALERT_MODE,
  RINGING_ALERT_MODES,
  CLOSED_CONVERSATION_STATUSES,
  CLOSED_TICKET_STATUSES,
  DEFAULT_SHORTCUTS,
  SHORTCUT_KEY_PATTERN,
  LIMITS,
};
